import { useApi } from '../../hooks/useApi';
import { Card, EmptyState, Loading, ModuleHero, Notice, StatCard, StatusPill } from '../../components/ui';
import NoCompany from '../../components/NoCompany';
import { PayButton } from '../../components/PayButton';
import { PAYMENT_STATUS, formatDate, formatDateTime, formatMoney } from '../../lib/format';
import type { FundingRecord } from '../../types/workspace';

type Response = {
  companyStatus: string;
  fundingAmount: string | null;
  successFeePercent: number | null;
  fundings: FundingRecord[];
};

type Totals = { funded: number; fee: number; paid: number; due: number };

function totalsByCurrency(fundings: FundingRecord[]): [string, Totals][] {
  const map = new Map<string, Totals>();
  fundings.forEach((f) => {
    const t = map.get(f.currency) || { funded: 0, fee: 0, paid: 0, due: 0 };
    t.funded += f.amount;
    t.fee += f.successFee;
    if (f.payment?.status === 'PAID') t.paid += f.payment.amount;
    else if (f.payment && f.payment.status !== 'CANCELLED') t.due += f.payment.amount;
    map.set(f.currency, t);
  });
  return Array.from(map);
}

export default function Funded() {
  const { data, loading, error, code, reload } = useApi<Response>('/customer/funded');

  if (loading) return <Loading />;
  if (code === 'NO_COMPANY') return <NoCompany />;
  if (error || !data) return <Notice tone="rose">{error}</Notice>;

  const totals = totalsByCurrency(data.fundings);

  return (
    <div>
      <ModuleHero
        eyebrow="Step 5 · Funded"
        title="Funding & Micus fee"
        description="Funds your partners disbursed, as recorded by your Micus analyst, and the Micus success fee that becomes payable on each disbursement."
        actions={
          data.successFeePercent != null ? <StatusPill label={`Success fee ${data.successFeePercent}%`} tone="pink" /> : undefined
        }
      />

      {data.fundings.length === 0 ? (
        <div className="mt-6">
          <EmptyState
            title="Not funded yet"
            body={`Once a partner disburses${data.fundingAmount ? ` against your ask of ${data.fundingAmount}` : ''}, your analyst records it here with the Micus success fee.`}
            cta={{ label: 'Open Partners', to: '/partners' }}
          />
        </div>
      ) : (
        <>
          {totals.map(([currency, t]) => (
            <div key={currency} className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
              <StatCard label="Total funded" value={formatMoney(t.funded, currency)} hint={data.fundingAmount ? `Ask: ${data.fundingAmount}` : undefined} tone="green" />
              <StatCard label="Micus success fee" value={formatMoney(t.fee, currency)} hint="on disbursed funds" />
              <StatCard label="Paid to Micus" value={formatMoney(t.paid, currency)} tone="blue" />
              <StatCard label="Due to Micus" value={formatMoney(t.due, currency)} tone={t.due ? 'amber' : 'slate'} to="/payments" />
            </div>
          ))}

          <div className="mt-6 space-y-3">
            {data.fundings.map((f) => {
              const st = f.payment ? PAYMENT_STATUS[f.payment.status] : null;
              return (
                <Card key={f.id}>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-[#c084a0]">
                        {f.partnerName}
                        {f.instrument ? ` · ${f.instrument}` : ''}
                      </p>
                      <p className="mt-0.5 text-2xl font-black text-[#065f46]">{formatMoney(f.amount, f.currency)}</p>
                      <p className="text-xs text-[#b0708f]">Disbursed {formatDate(f.fundedAt)}</p>
                      {f.note && <p className="mt-2 max-w-xl text-sm text-[#4a1a30]">{f.note}</p>}
                    </div>
                    <div className="min-w-[220px] rounded-xl px-4 py-3" style={{ background: '#fff5f8', border: '1px solid #fce7f3' }}>
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-[#c084a0]">Micus fee · {f.successFeePercent}%</p>
                        {st && <StatusPill label={st.label} tone={st.tone} />}
                      </div>
                      <p className="mt-0.5 text-lg font-black text-[#d6006e]">{formatMoney(f.successFee, f.currency)}</p>
                      {f.payment?.status === 'PAID' ? (
                        <p className="text-[11px] text-[#047857]">Paid {formatDateTime(f.payment.paidAt)}</p>
                      ) : f.payment && f.payment.status !== 'CANCELLED' ? (
                        <PayButton payment={f.payment} onPaid={() => void reload(true)} className="mt-2" />
                      ) : (
                        <p className="text-[11px] text-[#b0708f]">{f.payment ? 'Waived by Micus' : 'No fee applies'}</p>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
